import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  Wrap,
} from '@chakra-ui/react'
import { faClock, faInfoCircle, faPencil, faShieldCheck } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime, Duration } from 'luxon'
import { useCallback, useMemo, useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { noOp } from '@/utils/helpers'
import sleep from '@/utils/sleep'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import getLatestAgeAssuranceSession from '@/client/services/ageAssurance/getLatestAgeAssuranceSession'
import type { AgeAssuranceSessionConfig } from '@/client/services/ageAssurance/types'
import { getArticleUrl } from '@/client/services/url/faq'

import AgeAssuranceSessionModal, {
  type AgeAssuranceSessionModalResult,
} from '@/components/AgeAssurance/AgeAssuranceSessionModal'
import PublicImage from '@/components/PublicImage/PublicImage'
import ShwLink from '@/components/ShwLink'
import Alert from '@/components/UI/Alert'
import UpdateAgeModal from '@/components/UpdateAgeModal'

const YOTI_WAIT_TIME = Duration.fromObject({ milliseconds: 2500 })

type PrepReminderProps = {
  illustrationImageName: string
  reminder: string
  infoTooltip?: string
}

const PREP_REMINDER_PROPS: PrepReminderProps[] = [
  {
    illustrationImageName: 'well-lit-space.svg',
    reminder: 'Find a private, well-lit space',
  },
  {
    illustrationImageName: 'device-camera-mic.svg',
    reminder: 'Use a device with a camera and microphone',
  },
  {
    illustrationImageName: 'face-coverings.svg',
    reminder: 'Remove any face coverings',
    infoTooltip: 'Religious face coverings are exempt.',
  },
]

export default function AgeAssuranceGuide({
  showExitButton = false,
  onExitButtonClick,
}: {
  showExitButton?: boolean
  onExitButtonClick?: () => void
}) {
  const user = useUser()

  const age =
    user.dateOfBirth != null
      ? Math.floor(-DateTime.fromISO(user.dateOfBirth).diffNow('years').years)
      : null

  const [sessionHistory, setSessionHistory] = useState(user.ageAssuranceHistory)

  const latestSession = useMemo(
    () => getLatestAgeAssuranceSession(sessionHistory),
    [sessionHistory]
  )

  const {
    isOpen: isUpdateModalOpen,
    onClose: onUpdateModalClose,
    onOpen: onUpdateModalOpen,
  } = useDisclosure()
  const {
    isOpen: isSessionModalOpen,
    onClose: onSessionModalClose,
    onOpen: onSessionModalOpen,
  } = useDisclosure()

  const onSessionModalDone = useCallback(
    async (result: AgeAssuranceSessionModalResult) => {
      onSessionModalClose()

      if (result === 'later') {
        return
      }

      if (result === 'completed' && latestSession?.config.method === 'yoti') {
        // Give us a few seconds to receive and process Yoti's webhook
        setWaitingForYoti(true)
        await sleep(YOTI_WAIT_TIME.as('milliseconds'))
        setWaitingForYoti(false)
      }
      const newHistory = await RPC.getAgeAssuranceHistory({})
      setSessionHistory(newHistory)
    },
    [onSessionModalClose, latestSession?.config.method]
  )

  const [isWaitingForYoti, setWaitingForYoti] = useState(false)
  const [sessionConfig, setSessionConfig] = useState<AgeAssuranceSessionConfig | null>(null)

  const [startSession, isStartingSession] = useAPIWrapper(
    async () => {
      const session = await RPC.findOrCreateAgeAssuranceSession({})
      setSessionConfig(session.config)
      // Open the completion modal in a future tick to prevent a flashing effect
      // as the modal opens with session still set to null.
      setTimeout(() => {
        onSessionModalOpen()
      }, 0)
    },
    [onSessionModalOpen],
    'Start Age Assurance session'
  )

  const nextSessionIsYoti =
    latestSession == null ||
    (latestSession?.config.method === 'yoti' && latestSession?.status === 'pending')

  return (
    <Box>
      <UpdateAgeModal
        hideVerificationNotice
        isOpen={isUpdateModalOpen}
        onClose={onUpdateModalClose}
      />
      <AgeAssuranceSessionModal
        sessionConfig={sessionConfig ?? { method: 'videoask' }}
        onDone={onSessionModalDone}
        isOpen={isSessionModalOpen}
      />
      {latestSession?.status === 'completed' && latestSession?.outcome === 'success' ? (
        <Stack align="center" gap="16px">
          <Center width="80px" height="80px" bgColor="green.subtle" borderRadius="full">
            <Text color="green.fg" fontSize="32px">
              <FontAwesomeIcon icon={faShieldCheck} />
            </Text>
          </Center>
          <Stack align="center" gap="4px" maxWidth="400px">
            <Heading as="h3" size="md" textAlign="center">
              Age Verification Complete
            </Heading>
            <Text color="fg.muted" textAlign="center">
              Your age has been successfully verified. You can now attend live sessions on
              Schoolhouse!
            </Text>
          </Stack>
          {showExitButton ? (
            <Button width="100%" onClick={onExitButtonClick != null ? onExitButtonClick : noOp}>
              Got It
            </Button>
          ) : null}
        </Stack>
      ) : latestSession?.config.method === 'videoask' && latestSession?.status === 'pending' ? (
        <Stack align="center" gap="16px">
          <Center width="80px" height="80px" bgColor="bg.subtle" borderRadius="full">
            <Text color="fg.muted" fontSize="32px">
              <FontAwesomeIcon icon={faClock} />
            </Text>
          </Center>
          <Stack align="center" gap="4px" maxWidth="400px">
            <Heading as="h3" size="md" textAlign="center">
              Manual Verification In Progress
            </Heading>
            <Text color="fg.muted" textAlign="center">
              If you&apos;ve submitted your VideoAsk, our Safety team will review it within 2
              business days.
            </Text>
          </Stack>
          <Text color="fg.muted" fontSize="sm" textAlign="center" mb="12px">
            Haven&apos;t submitted?{' '}
            <ShwLink onClick={startSession} cursor="pointer">
              Start your submission
            </ShwLink>
            {' · '}
            Having trouble?{' '}
            <ShwLink href={getArticleUrl('how-do-i-contact-the-schoolhouse-team')}>
              Contact User Support
            </ShwLink>
          </Text>
          {showExitButton ? (
            <Button width="100%" onClick={onExitButtonClick != null ? onExitButtonClick : noOp}>
              Got It
            </Button>
          ) : null}
        </Stack>
      ) : (
        <Stack gap="32px" opacity={isWaitingForYoti ? 0.4 : 1} transition="opacity 0.15s">
          {nextSessionIsYoti ? (
            // Yoti
            <Text>
              Before new users can join sessions, we ask them to verify their age. You can learn
              more about our policy{' '}
              <ShwLink href={getArticleUrl('age-verification-for-new-users')}>here</ShwLink>.
            </Text>
          ) : (
            // VideoAsk
            <Stack gap="12px">
              <Alert variant="warning">
                There was an issue verifying your age through our verification provider.
              </Alert>
              <Stack gap="4px">
                <Text>
                  To successfully verify your age, you&apos;ll need to complete our alternate manual
                  video verification process.
                </Text>
                <Text fontSize="sm" color="fg.muted">
                  Our Safety team will review your submission within 2 business days.
                </Text>
              </Stack>
            </Stack>
          )}
          <Wrap justify="center" spacingX="50px" spacingY="32px" justifyContent="center">
            {PREP_REMINDER_PROPS.map(({ illustrationImageName, reminder, infoTooltip }) => (
              <Stack key={reminder} alignItems="center" width="164px">
                <Center width="164px" height="164px" bgColor="bg.subtle" borderRadius="8px">
                  <PublicImage width="100px" src={`/age-assurance/${illustrationImageName}`} />
                </Center>
                <Text textAlign="center" color="fg.muted" fontSize="14px" lineHeight="20px">
                  {reminder}
                  {infoTooltip != null ? (
                    <Tooltip label={infoTooltip}>
                      <Box ml="4px" display="inline-block" color="fg">
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </Box>
                    </Tooltip>
                  ) : null}
                </Text>
              </Stack>
            ))}
          </Wrap>
          <Stack gap="24px" alignItems="stretch">
            <Flex
              alignItems="center"
              justifyContent="space-between"
              bgColor="bg.subtle"
              borderRadius="12px"
              border={`2px solid ${chakraTokenToVar('border')}`}
              px="24px"
              py="16px"
            >
              <Flex alignItems="center" gap="12px">
                <Heading color="fg.muted" size="lg">
                  My age is
                </Heading>
                <Heading size="2xl">{age ?? '—'}</Heading>
              </Flex>
              <Button
                variant="ghost"
                leftIcon={<FontAwesomeIcon icon={faPencil} />}
                onClick={onUpdateModalOpen}
              >
                Edit
              </Button>
            </Flex>
            <Button width="100%" onClick={startSession} isLoading={isStartingSession}>
              {nextSessionIsYoti ? 'Start Verification' : 'Start Manual Verification'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  )
}
